import { CrmNoteType, CrmTaskStatus } from '@plato/data/prisma';
import { z } from 'zod';
import type { CrmNoteDTO } from '../dto/crm-note-dto.ts';
import type { CrmTaskDTO } from '../dto/crm-task-dto.ts';
import { CustomerBusinessStatus } from '../dto/customer-dto.ts';
import type { InsInsightOutcomeDTO } from '../dto/ins-insight-outcome-dto.ts';
import type { SalesOrderListRow } from '../starrocks/starrocks-entity-builders-list.ts';

export type ActivityFeedRecord = {
    activity_id: string;
    customer_id: string;
    customer_name?: string;
    model_id: string;
    model_type: ActivityFeedModelType;
    event_type: string;

    payload?: Record<string, any>;
    created_at?: string;
    cursor: string;
};

/** A QUOTE or ORDER entry's payload. */
export type ActivityFeedQuoteOrderPayload = SalesOrderListRow;

/**
 * A VISIT note's payload: the note plus the tasks and insight outcomes logged on it, and the
 * quotes those tasks link to.
 */
export type ActivityFeedVisitPayload = CrmNoteDTO & {
    tasks: CrmTaskDTO[];
    outcomes: InsInsightOutcomeDTO[];
    quotes: ActivityFeedQuoteOrderPayload[];
};

export const ActivityFeedModelType = {
    ORDER: 'ORDER',
    QUOTE: 'QUOTE',
    TASK: 'TASK',
    NOTE: 'NOTE',
    INSIGHT_OUTCOME: 'INSIGHT_OUTCOME',
    CUSTOMER_STATUS_CHANGE: 'CUSTOMER_STATUS_CHANGE',
} as const;

export type ActivityFeedModelType = (typeof ActivityFeedModelType)[keyof typeof ActivityFeedModelType];

export const OrderStatusFilterOptions = {
    OPEN: 'OPEN',
    PARTIALLY_BILLED: 'PARTIALLY_BILLED',
    BILLED: 'BILLED',
    PROCESSED_INTERNALLY: 'PROCESSED_INTERNALLY',
    OPEN_IN_PICKING: 'OPEN_IN_PICKING',
    PARTIALLY_PICKED: 'PARTIALLY_PICKED',
    PICKED: 'PICKED',
    PARTIALLY_SHIPPED: 'PARTIALLY_SHIPPED',
    RELEASED_FOR_DELIVERY: 'RELEASED_FOR_DELIVERY',
    SHIPPED: 'SHIPPED',
    RETURNED: 'RETURNED',
    CANCELLED: 'CANCELLED',
} as const;

export type OrderStatusFilterOptions =
    (typeof OrderStatusFilterOptions)[keyof typeof OrderStatusFilterOptions];

export const QuoteStatusFilterOptions = {
    OPEN: 'OPEN',
    PARTIALLY_RECEIVED: 'PARTIALLY_RECEIVED',
    RECEIVED: 'RECEIVED',
    LOST: 'LOST',
    REVISED: 'REVISED',
    OTHER: 'OTHER',
} as const;

export type QuoteStatusFilterOptions =
    (typeof QuoteStatusFilterOptions)[keyof typeof QuoteStatusFilterOptions];

export const InsightOutcomeResponseFilterOptions = {
    INTERESTED: 'INTERESTED',
    NO_NEED: 'NO_NEED',
    BUYS_ELSEWHERE: 'BUYS_ELSEWHERE',
} as const;

export type InsightOutcomeResponseFilterOptions =
    (typeof InsightOutcomeResponseFilterOptions)[keyof typeof InsightOutcomeResponseFilterOptions];

export const feedFilterSchema = z.object({
    customerIds: z.array(z.string().min(1)).optional(),
    modelType: z.enum(ActivityFeedModelType).optional(),
    modelTypes: z.array(z.enum(ActivityFeedModelType)).min(1).optional(),
    searchText: z.string().optional(),
    orderStatuses: z.array(z.enum(OrderStatusFilterOptions)).default([]),
    quoteStatuses: z.array(z.enum(QuoteStatusFilterOptions)).default([]),
    taskStatuses: z.array(z.enum(CrmTaskStatus)).default([]),
    noteTypes: z.array(z.enum(CrmNoteType)).default([]),
    insightOutcomeResponses: z.array(z.enum(InsightOutcomeResponseFilterOptions)).default([]),
    customerStatuses: z.array(CustomerBusinessStatus).default([]),
    userIds: z.array(z.string()).default([]),
});

export type FeedFilterInput = z.infer<typeof feedFilterSchema>;

export const getFeedListWithFilterSchema = z.object({
    customerId: z.string().min(1).optional(),
    filter: feedFilterSchema,
    cursor: z.string().nullish(),
    page_size: z.number().optional().default(20),
});
